import { z } from "zod"

const emailField = z
  .email("Invalid email address")
  .min(1, "Email is required field")

// BioT-enforced password rule: 8+ chars, at least one lowercase, one
// uppercase, one digit. Validating client-side gives the same error as the
// server would, so the user gets immediate feedback instead of a round-trip.
const bioTPasswordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d$@!%*#?&\-_]{8,}$/

const passwordField = z
  .string()
  .min(1, "Password is required field")
  .refine((password) => bioTPasswordRegex.test(password), {
    message:
      "Password must have at least 8 characters that include at least 1 lowercase character, 1 uppercase character, and 1 number",
  })

export const signInSchema = z.object({
  email: emailField,
  password: z.string().min(1, "Please enter your password"),
})

export type SignInFormData = z.infer<typeof signInSchema>

export const signUpSchema = z
  .object({
    password: passwordField,
    passwordConfirmation: z.string().min(1, "Please confirm your password"),
    agreeToTerms: z.literal(true, {
      error: "You must agree to the terms and conditions",
    }),
    agreeToTextMessages: z.literal(true, {
      error: "You must agree to receive text messages",
    }),
  })
  .refine(({ password, passwordConfirmation }) => password === passwordConfirmation, {
    message: "Passwords do not match",
    path: ["passwordConfirmation"],
  })

export type SignUpFormData = z.infer<typeof signUpSchema>

export const forgotPasswordSchema = z.object({
  email: emailField,
})

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export const resetPasswordSchema = z
  .object({
    password: passwordField,
    passwordConfirmation: z.string().min(1, "Please confirm your password"),
  })
  .refine(({ password, passwordConfirmation }) => password === passwordConfirmation, {
    message: "Passwords do not match",
    path: ["passwordConfirmation"],
  })

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export const requestInvitationSchema = z.object({
  email: emailField,
})

export type RequestInvitationFormData = z.infer<typeof requestInvitationSchema>
